import compare from "compare-versions";

import { Action, actionCreators } from "../actions";
import { electronDownloadVersion } from "../utils/config";

export interface Config {
    APP_VERSION: string | undefined;
    ELECTRON_APP_VERSION: string | undefined;
}

export interface Store {
    loading: boolean;
    newVersion: string | undefined;
    newElectronVersion: string;
    realmVersion: string | undefined;
    serverVersion: string | undefined;
    messageUpdateExpiration: number | undefined;
    maxUploadsSizeBytes: number;
    maxRosterRequestSize: number;
}

export function initialStore(): Store {
    return {
        loading: false,
        newVersion: undefined,
        newElectronVersion: electronDownloadVersion,
        realmVersion: undefined,
        serverVersion: undefined,
        messageUpdateExpiration: undefined,
        maxUploadsSizeBytes: 104857600, // max_uploads_size_bytes: 104_857_600 from domain/config/config.exs:
        maxRosterRequestSize: 1000,
    };
}

export function update(store = initialStore(), action: Action): Store {
    switch (action.type) {
        case actionCreators.loadConfigStarted.type: {
            return { ...store, loading: true };
        }

        case actionCreators.loadConfigFailed.type: {
            return { ...store, loading: false };
        }

        case actionCreators.loadConfigSuccess.type: {
            const config = action.payload;
            return {
                ...store,
                loading: false,
                newVersion: config.APP_VERSION,
                newElectronVersion: config.ELECTRON_APP_VERSION || electronDownloadVersion,
            };
        }

        case actionCreators.onOnline.type: {
            const data = action.payload;
            const serverVersion = `${data.server_version}-${data.published_version}`;
            const messageUpdateExpiration = data.limits.message_change_expiration_us;
            const maxUploadsSizeBytes = data.limits.max_uploads_size_bytes;
            const maxRosterRequestSize =
                data.limits.roster_q_max_limit || store.maxRosterRequestSize;
            return {
                ...store,
                serverVersion,
                messageUpdateExpiration,
                maxUploadsSizeBytes,
                maxRosterRequestSize,
            };
        }

        case actionCreators.onDiscover.type: {
            const data = action.payload;
            const realmVersion =
                data && "server_version" in data
                    ? `${data.server_version}-${data.published_version}`
                    : undefined;
            return { ...store, realmVersion };
        }

        default: {
            return store;
        }
    }
}

export type OuterStore = { config: Store };

export function isLoading(store: OuterStore) {
    return store.config.loading;
}

function safeCompare(v1: string, v2: string) {
    try {
        return compare(v1, v2);
    } catch (e) {
        console.error("failed to compare versions", v1, v2, e);
        return;
    }
}

export function hasNewVersion(store: OuterStore, version: string) {
    const { newVersion } = store.config;
    if (newVersion && version !== newVersion) {
        const newWithoutSha = newVersion.split("-")[0];
        const compare = safeCompare(newWithoutSha, version.split("-")[0]);
        if (compare === 1) {
            return { type: "upgrade", version: newWithoutSha };
        } else if (compare === 0) {
            return { type: "upgrade", version: newVersion };
        } else if (compare === -1) {
            return { type: "downgrade", version: newWithoutSha };
        }
    }
    return;
}

export function hasNewElectronVersion(store: OuterStore, version: string) {
    const { newElectronVersion } = store.config;
    if (
        newElectronVersion &&
        version !== newElectronVersion &&
        safeCompare(newElectronVersion, version) !== -1
    ) {
        return newElectronVersion;
    }
    return;
}

export function getRealmVersion(store: OuterStore) {
    return store.config.realmVersion;
}

export function getServerVersion(store: OuterStore) {
    return store.config.serverVersion;
}

export function getUpdateExpiration(store: OuterStore) {
    return store.config.messageUpdateExpiration === undefined
        ? undefined
        : store.config.messageUpdateExpiration / 1000;
}

export function isUpdateExpired(store: OuterStore, ts: number) {
    return store.config.messageUpdateExpiration === undefined
        ? false
        : ts + store.config.messageUpdateExpiration <= Date.now() * 1000;
}

export function getMaxUploadsSizeBytes(store: OuterStore) {
    return store.config.maxUploadsSizeBytes;
}

export function getMaxRosterRequestSize(store: OuterStore) {
    return store.config.maxRosterRequestSize;
}
